import type { JarvisTransientState } from '@kaizen/shared';
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef
} from 'react';

import type {
  RealtimeChannel
} from '@supabase/supabase-js';

import {
  Device,
  DeviceStatus,
  DeviceServicesStatus,
  LatencyQuality,
  Job,
  RealtimeEnvelope,
  PingPayload,
  PongPayload,
  DeviceHeartbeatPayload,
  BrowseDirectoryRequestPayload,
  BrowseDirectoryResultPayload,
  ArtifactContentRequestPayload,
  ArtifactContentResultPayload,
  JarvisRequestPayload,
  JarvisResponsePayload,
  ChatGPTConversationIdentity,
  calculateLatencyQuality
} from '@kaizen/shared';

import {
  supabase,
  isSupabaseConfigured
} from '../lib/supabase';

import {
  useAuth
} from './AuthContext';


export interface LogLine {
  id: string;

  sequence: number;

  stream:
    'stdout' |
    'stderr' |
    'system';

  data:
    string;

  timestamp:
    string;
}


interface TextFileReadRequestPayload {
  requestId: string;
  jobId: string;
  path: string;
}


interface TextFileReadResultPayload {
  requestId: string;
  jobId: string;
  path: string;
  success: boolean;
  filename?: string;
  size?: number;
  content?: string;
  error?: string;
}

interface RealtimeContextType {
  device:
    Device |
    null;

  deviceStatus:
    DeviceStatus;

  services:
    DeviceServicesStatus;

  latencyMs:
    number |
    null;

  wifiSsid:
    string |
    null;

  latencyQuality:
    LatencyQuality;

  activeJob:
    Job |
    null;

  activeJobLogs:
    LogLine[];

  clearLogs:
    () => void;

  sendJob:
    (
      job:
        Omit<
          Job,
          'id' |
          'userId' |
          'status' |
          'createdAt'
        >
    ) =>
      Promise<Job>;

  cancelActiveJob:
    () =>
      Promise<boolean>;

  pingDevice:
    () =>
      Promise<{
        success: boolean;
        rttMs?: number;
        error?: string;
      }>;

  browseDirectory:
    (
      path?: string
    ) =>
      Promise<BrowseDirectoryResultPayload>;

  getArtifactContent:
    (
      artifactId: string
    ) =>
      Promise<ArtifactContentResultPayload>;

  readTextFile:
    (
      path: string,
      jobId: string
    ) =>
      Promise<TextFileReadResultPayload>;

  jarvisChat:
    ChatGPTConversationIdentity |
    null;

  jarvisBusy:
    boolean;

  jarvisTransientState:
    JarvisTransientState;

  sendJarvisMessage:
    (
      message: string,
      context?: {
        projectId?: string;
        workingDirectory?: string;
      }
    ) =>
      Promise<JarvisResponsePayload>;
}


const RealtimeContext =
  createContext<
    RealtimeContextType |
    undefined
  >(
    undefined
  );


function mapDevice(
  row:
    any
): Device {
  return {
    id:
      row.id,

    userId:
      row.user_id,

    name:
      row.name,

    platform:
      row.platform,

    agentVersion:
      row.agent_version,

    status:
      row.status,

    lastSeenAt:
      row.last_seen_at,

    osInfo:
      row.os_info,

    allowedRoots:
      row.allowed_roots ||
      [],

    createdAt:
      row.created_at,

    updatedAt:
      row.updated_at
  };
}


async function subscribePrivateChannel(
  channel:
    RealtimeChannel
): Promise<void> {
  await new Promise<void>(
    (
      resolve,
      reject
    ) => {
      const timeout =
        setTimeout(
          () =>
            reject(
              new Error(
                'Realtime subscription timed out'
              )
            ),
          15000
        );

      channel.subscribe(
        status => {
          if (
            status ===
            'SUBSCRIBED'
          ) {
            clearTimeout(
              timeout
            );

            resolve();
          }
          else if (
            status ===
              'CHANNEL_ERROR' ||
            status ===
              'TIMED_OUT'
          ) {
            clearTimeout(
              timeout
            );

            reject(
              new Error(
                `Realtime subscription failed: ${status}`
              )
            );
          }
        }
      );
    }
  );
}


export const RealtimeProvider:
React.FC<{
  children:
    React.ReactNode
}> = ({
  children
}) => {
  const {
    user
  } =
    useAuth();

  const [
    device,
    setDevice
  ] =
    useState<
      Device |
      null
    >(
      null
    );

  const [
    deviceStatus,
    setDeviceStatus
  ] =
    useState<DeviceStatus>(
      'offline'
    );

  const [
    services,
    setServices
  ] =
    useState<DeviceServicesStatus>(
      {
        agent:
          'disconnected',

        powershell:
          'unavailable',

        chromeExtension:
          'unavailable',

        chatgptBridge:
          'unavailable'
      }
    );

  const [
    jarvisChat,
    setJarvisChat
  ] =
    useState<
      ChatGPTConversationIdentity |
      null
    >(
      null
    );


  const [
    jarvisBusy,
    setJarvisBusy
  ] =
    useState<boolean>(
      false
    );


  const [
    jarvisTransientState,
    setJarvisTransientState
  ] =
    useState<JarvisTransientState>(
      'idle'
    );

  const [
    latencyMs,
    setLatencyMs
  ] =
    useState<
      number |
      null
    >(
      null
    );

  const [
    latencyQuality,
    setLatencyQuality
  ] =
    useState<LatencyQuality>(
      'unknown'
    );


  const [
    wifiSsid,
    setWifiSsid
  ] =
    useState<
      string |
      null
    >(
      null
    );


  const [
    activeJob,
    setActiveJob
  ] =
    useState<
      Job |
      null
    >(
      null
    );

  const [
    activeJobLogs,
    setActiveJobLogs
  ] =
    useState<
      LogLine[]
    >(
      []
    );


  const activeJobRef =
    useRef<
      Job |
      null
    >(
      null
    );

  const deviceRef =
    useRef<
      Device |
      null
    >(
      null
    );

  const deviceChannelRef =
    useRef<
      RealtimeChannel |
      null
    >(
      null
    );

  const jobChannelRef =
    useRef<
      RealtimeChannel |
      null
    >(
      null
    );

  const lastHeartbeatRef =
    useRef<number>(
      0
    );

  const pendingBrowseRequestsRef =
    useRef<
      Map<
        string,
        {
          resolve: (
            result:
              BrowseDirectoryResultPayload
          ) => void;

          reject: (
            error:
              Error
          ) => void;

          timer:
            ReturnType<
              typeof setTimeout
            >;
        }
      >
    >(
      new Map()
    );

  const pendingArtifactRequestsRef =
    useRef<
      Map<
        string,
        {
          resolve: (
            result:
              ArtifactContentResultPayload
          ) => void;

          reject: (
            error:
              Error
          ) => void;

          timer:
            ReturnType<
              typeof setTimeout
            >;
        }
      >
    >(
      new Map()
    );

  const pendingTextFileRequestsRef =
    useRef<
      Map<
        string,
        {
          resolve:
            (
              result:
                TextFileReadResultPayload
            ) => void;

          reject:
            (
              error:
                Error
            ) => void;

          timer:
            ReturnType<
              typeof setTimeout
            >;
        }
      >
    >(
      new Map()
    );

  const pendingJarvisRequestsRef =
    useRef<
      Map<
        string,
        {
          resolve:
            (
              result:
                JarvisResponsePayload
            ) => void;

          reject:
            (
              error:
                Error
            ) => void;

          timer:
            ReturnType<
              typeof setTimeout
            >;
        }
      >
    >(
      new Map()
    );

  activeJobRef.current =
    activeJob;

  deviceRef.current =
    device;


  const clearLogs =
    useCallback(
      () => {
        setActiveJobLogs(
          []
        );
      },
      []
    );


  /*
   * Discover the authenticated user's registered KAIZEN device.
   *
   * Agent registration is authoritative.
   */
  useEffect(
    () => {
      if (
        !isSupabaseConfigured ||
        !user?.id
      ) {
        setDevice(
          null
        );

        setDeviceStatus(
          'offline'
        );

        return;
      }

      let stopped =
        false;

      const refresh =
        async () => {
          const {
            data,
            error
          } =
            await supabase
              .from(
                'kr_devices'
              )
              .select(
                '*'
              )
              .eq(
                'user_id',
                user.id
              )
              .order(
                'last_seen_at',
                {
                  ascending:
                    false
                }
              )
              .limit(
                1
              )
              .maybeSingle();

          if (
            stopped
          ) {
            return;
          }

          if (
            error
          ) {
            console.error(
              'KAIZEN device query failed:',
              error
            );

            return;
          }

          if (
            !data
          ) {
            setDevice(
              null
            );

            setDeviceStatus(
              'offline'
            );

            return;
          }

          const mapped =
            mapDevice(
              data
            );

          setDevice(
            mapped
          );

          const age =
            Date.now() -
            new Date(
              mapped.lastSeenAt
            ).getTime();

          if (
            age <=
            45000
          ) {
            setDeviceStatus(
              'online'
            );

            lastHeartbeatRef.current =
              new Date(
                mapped.lastSeenAt
              ).getTime();
          }
          else {
            setDeviceStatus(
              'offline'
            );
          }
        };

      void refresh();

      const timer =
        setInterval(
          () => {
            void refresh();
          },
          10000
        );

      return () => {
        stopped =
          true;

        clearInterval(
          timer
        );
      };
    },
    [
      user?.id
    ]
  );


  /*
   * Device private channel.
   */
  useEffect(
    () => {
      if (
        !isSupabaseConfigured ||
        !user?.id ||
        !device?.id
      ) {
        return;
      }

      let disposed =
        false;

      const channel =
        supabase.channel(
          `device:${device.id}`,
          {
            config: {
              private:
                true,

              broadcast: {
                ack:
                  true
              }
            }
          }
        );


      channel.on(
        'broadcast',
        {
          event:
            'device_heartbeat'
        },
        (
          {
            payload
          }:
          {
            payload:
              RealtimeEnvelope<DeviceHeartbeatPayload>
          }
        ) => {
          const now =
            Date.now();

          lastHeartbeatRef.current =
            now;

          setWifiSsid(
            payload.payload.wifiSsid ??
              null
          );

          setDeviceStatus(
            'online'
          );

          setServices(
            {
              agent:
                payload.payload.services.agent as any,

              powershell:
                payload.payload.services.powershell as any,

              chromeExtension:
                payload.payload.services.chromeExtension as any,

              chatgptBridge:
                payload.payload.services.chatgptBridge as any
            }
          );

          setJarvisChat(
            payload.payload.jarvis?.chat ||
            null
          );


          setJarvisBusy(
            payload.payload.jarvis?.busy ===
              true
          );

          setDevice(
            previous =>
              previous
                ? {
                    ...previous,

                    status:
                      'online',

                    lastSeenAt:
                      payload.payload.timestamp,

                    agentVersion:
                      payload.payload.agentVersion,

                    osInfo:
                      payload.payload.osInfo
                  }
                : previous
          );
        }
      );


      channel.on(
        'broadcast',
        {
          event:
            'pong'
        },
        (
          {
            payload
          }:
          {
            payload:
              RealtimeEnvelope<PongPayload>
          }
        ) => {
          const rtt =
            Date.now() -
            payload.payload.clientTimestamp;

          setLatencyMs(
            rtt
          );

          setLatencyQuality(
            calculateLatencyQuality(
              rtt
            )
          );
        }
      );




      channel.on(
        'broadcast',
        {
          event:
            'assistant_status'
        },
        (
          {
            payload
          }:
          {
            payload:
              RealtimeEnvelope<{
                transientState?:
                  JarvisTransientState;
                chat?:
                  ChatGPTConversationIdentity;
                observedAt?:
                  string;
              }>
          }
        ) => {

          const transientState =
            payload?.payload?.transientState;


          setJarvisTransientState(
            transientState === 'thinking' ||
            transientState === 'working' ||
            transientState === 'generating'
              ? transientState
              : 'idle'
          );


          if (
            payload?.payload?.chat
          ) {
            setJarvisChat(
              payload.payload.chat
            );
          }
        }
      );

      channel.on(
        'broadcast',
        {
          event:
            'assistant_response'
        },
        (
          {
            payload
          }:
          {
            payload:
              RealtimeEnvelope<JarvisResponsePayload>
          }
        ) => {

          const result =
            payload?.payload;


          if (
            !result?.requestId
          ) {
            return;
          }


          const pending =
            pendingJarvisRequestsRef.current.get(
              result.requestId
            );


          if (
            !pending
          ) {
            return;
          }


          clearTimeout(
            pending.timer
          );


          pendingJarvisRequestsRef.current.delete(
            result.requestId
          );


          if (
            result.chat
          ) {

            setJarvisChat(
              result.chat
            );
          }


          setJarvisBusy(
            false
          );


          pending.resolve(
            result
          );
        }
      );

      channel.on(
        'broadcast',
        {
          event:
            'browse_directory_result'
        },
        (
          {
            payload
          }:
          {
            payload:
              RealtimeEnvelope<BrowseDirectoryResultPayload>
          }
        ) => {
          const result =
            payload?.payload;

          if (
            !result?.requestId
          ) {
            return;
          }

          const pending =
            pendingBrowseRequestsRef.current.get(
              result.requestId
            );

          if (
            !pending
          ) {
            return;
          }

          clearTimeout(
            pending.timer
          );

          pendingBrowseRequestsRef.current.delete(
            result.requestId
          );

          pending.resolve(
            result
          );
        }
      );

      channel.on(
        'broadcast',
        {
          event:
            'artifact_content_result'
        },
        (
          {
            payload
          }:
          {
            payload:
              RealtimeEnvelope<ArtifactContentResultPayload>
          }
        ) => {
          const result =
            payload?.payload;

          if (
            !result?.requestId
          ) {
            return;
          }

          const pending =
            pendingArtifactRequestsRef.current.get(
              result.requestId
            );

          if (
            !pending
          ) {
            return;
          }

          clearTimeout(
            pending.timer
          );

          pendingArtifactRequestsRef.current.delete(
            result.requestId
          );

          pending.resolve(
            result
          );
        }
      );

      channel.on(
        'broadcast',
        {
          event:
            'text_file_read_result'
        },
        (
          {
            payload
          }:
          {
            payload:
              RealtimeEnvelope<TextFileReadResultPayload>
          }
        ) => {

          const result =
            payload?.payload;


          if (
            !result?.requestId
          ) {
            return;
          }


          const pending =
            pendingTextFileRequestsRef.current.get(
              result.requestId
            );


          if (
            !pending
          ) {
            return;
          }


          clearTimeout(
            pending.timer
          );


          pendingTextFileRequestsRef.current.delete(
            result.requestId
          );


          pending.resolve(
            result
          );
        }
      );

      void subscribePrivateChannel(
        channel
      )
        .then(
          async () => {
            if (
              disposed
            ) {
              return;
            }

            deviceChannelRef.current =
              channel;

            const pingPayload:
              PingPayload = {
              id:
                `ping-${Date.now()}`,

              timestamp:
                Date.now()
            };

            await channel.send(
              {
                type:
                  'broadcast',

                event:
                  'ping',

                payload: {
                  id:
                    `env-${crypto.randomUUID()}`,

                  type:
                    'ping',

                  source:
                    'web',

                  timestamp:
                    new Date().toISOString(),

                  payload:
                    pingPayload
                }
              }
            );
          }
        )
        .catch(
          error => {
            console.error(
              'KAIZEN private device channel failed:',
              error
            );

            setDeviceStatus(
              'offline'
            );
          }
        );


      const pingTimer =
        setInterval(
          async () => {
            if (
              deviceChannelRef.current !==
              channel
            ) {
              return;
            }

            const pingPayload:
              PingPayload = {
              id:
                `ping-${Date.now()}`,

              timestamp:
                Date.now()
            };

            await channel.send(
              {
                type:
                  'broadcast',

                event:
                  'ping',

                payload: {
                  id:
                    `env-${crypto.randomUUID()}`,

                  type:
                    'ping',

                  source:
                    'web',

                  timestamp:
                    new Date().toISOString(),

                  payload:
                    pingPayload
                }
              }
            );
          },
          20000
        );


      const offlineTimer =
        setInterval(
          () => {
            if (
              lastHeartbeatRef.current ===
              0
            ) {
              return;
            }

            const age =
              Date.now() -
              lastHeartbeatRef.current;

            if (
              age >
              45000
            ) {
              setDeviceStatus(
                'offline'
              );

              setWifiSsid(
                null
              );

              setLatencyMs(
                null
              );

              setLatencyQuality(
                'unknown'
              );

              setServices(
                {
                  agent:
                    'disconnected',

                  powershell:
                    'unavailable',

                  chromeExtension:
                    'unavailable',

                  chatgptBridge:
                    'unavailable'
                }
              );
            }
          },
          5000
        );


      return () => {
        disposed =
          true;

        clearInterval(
          pingTimer
        );

        clearInterval(
          offlineTimer
        );

        if (
          deviceChannelRef.current ===
          channel
        ) {
          deviceChannelRef.current =
            null;
        }

        for (
          const [
            requestId,
            pending
          ]
          of pendingBrowseRequestsRef.current
        ) {
          clearTimeout(
            pending.timer
          );

          pending.reject(
            new Error(
              'Device channel closed before folder browse completed'
            )
          );

          pendingBrowseRequestsRef.current.delete(
            requestId
          );
        }

        for (
          const [
            requestId,
            pending
          ]
          of pendingArtifactRequestsRef.current
        ) {
          clearTimeout(
            pending.timer
          );

          pending.reject(
            new Error(
              'Device channel closed before artifact retrieval completed'
            )
          );

          pendingArtifactRequestsRef.current.delete(
            requestId
          );
        }

        for (
          const [
            requestId,
            pending
          ]
          of pendingTextFileRequestsRef.current
        ) {

          clearTimeout(
            pending.timer
          );


          pending.reject(
            new Error(
              'Device channel closed before text-file retrieval completed'
            )
          );


          pendingTextFileRequestsRef.current.delete(
            requestId
          );
        }

        for (
          const [
            requestId,
            pending
          ]
          of pendingJarvisRequestsRef.current
        ) {

          clearTimeout(
            pending.timer
          );


          pending.reject(
            new Error(
              'Device channel closed before JARVIS request completed'
            )
          );


          pendingJarvisRequestsRef.current.delete(
            requestId
          );
        }


        setJarvisBusy(
          false
        );

        void channel.unsubscribe();
      };
    },
    [
      user?.id,
      device?.id
    ]
  );


  const pingDevice = useCallback(async (): Promise<{
    success: boolean;
    rttMs?: number;
    error?: string;
  }> => {
    if (!isSupabaseConfigured) {
      return { success: false, error: 'Supabase is not configured' };
    }

    if (!user?.id) {
      return { success: false, error: 'Authentication required' };
    }

    const currentDevice = deviceRef.current;
    const channel = deviceChannelRef.current;

    if (!currentDevice || !channel) {
      return { success: false, error: 'KAIZEN Agent is not connected' };
    }

    if (deviceStatus !== 'online') {
      return { success: false, error: 'KAIZEN Agent device is offline' };
    }

    const pingId = `ping-${Date.now()}`;
    const pingStarted = Date.now();

    try {
      let pongResolve!: (val: any) => void;
      let pongReject!: (err: Error) => void;

      const pongPromise = new Promise<any>((resolve, reject) => {
        const timer = setTimeout(
          () => reject(new Error('Ping timed out after 5000ms')),
          5000
        );
        pongResolve = (val) => {
          clearTimeout(timer);
          resolve(val);
        };
        pongReject = (err) => {
          clearTimeout(timer);
          reject(err);
        };
      });

      // Register temporary pong listener BEFORE sending ping
      channel.on(
        'broadcast',
        { event: 'pong' },
        ({ payload }: { payload: any }) => {
          if (
            payload?.payload?.id === pingId ||
            payload?.payload?.clientTimestamp === pingStarted
          ) {
            pongResolve(payload);
          }
        }
      );

      const pingResult = await channel.send({
        type: 'broadcast',
        event: 'ping',
        payload: {
          id: `env-${crypto.randomUUID()}`,
          type: 'ping',
          source: 'web',
          timestamp: new Date().toISOString(),
          payload: {
            id: pingId,
            timestamp: pingStarted
          }
        }
      });

      if (pingResult !== 'ok') {
        throw new Error(`Ping dispatch failed: ${pingResult}`);
      }

      await pongPromise;
      const rtt = Date.now() - pingStarted;

      setLatencyMs(rtt);
      setLatencyQuality(calculateLatencyQuality(rtt));

      return { success: true, rttMs: rtt };
    } catch (err: any) {
      const sanitizedError = err?.message || 'Ping failed';
      return { success: false, error: sanitizedError };
    }
  }, [user?.id, deviceStatus]);



  const browseDirectory =
    useCallback(
      async (
        directoryPath?:
          string
      ): Promise<BrowseDirectoryResultPayload> => {
        if (
          !isSupabaseConfigured
        ) {
          throw new Error(
            'Supabase is not configured'
          );
        }

        if (
          !user?.id
        ) {
          throw new Error(
            'Authentication required'
          );
        }

        const currentDevice =
          deviceRef.current;

        const channel =
          deviceChannelRef.current;

        if (
          !currentDevice ||
          !channel
        ) {
          throw new Error(
            'KAIZEN Agent is not connected'
          );
        }

        if (
          deviceStatus !==
          'online'
        ) {
          throw new Error(
            'KAIZEN Agent device is offline'
          );
        }

        const requestId =
          crypto.randomUUID();

        const request:
          BrowseDirectoryRequestPayload = {
          requestId,

          path:
            directoryPath
        };


        const responsePromise =
          new Promise<BrowseDirectoryResultPayload>(
            (
              resolve,
              reject
            ) => {
              const timer =
                setTimeout(
                  () => {
                    pendingBrowseRequestsRef.current.delete(
                      requestId
                    );

                    reject(
                      new Error(
                        'Laptop folder browser timed out'
                      )
                    );
                  },
                  10000
                );

              pendingBrowseRequestsRef.current.set(
                requestId,
                {
                  resolve,
                  reject,
                  timer
                }
              );
            }
          );


        try {
          const sendResult =
            await channel.send(
              {
                type:
                  'broadcast',

                event:
                  'browse_directory_request',

                payload: {
                  id:
                    `browse-${requestId}`,

                  type:
                    'browse_directory_request',

                  source:
                    'web',

                  targetId:
                    currentDevice.id,

                  timestamp:
                    new Date().toISOString(),

                  payload:
                    request
                }
              }
            );


          if (
            sendResult !==
            'ok'
          ) {
            throw new Error(
              `Folder browse dispatch failed: ${sendResult}`
            );
          }
        }
        catch (
          error:
            unknown
        ) {
          const pending =
            pendingBrowseRequestsRef.current.get(
              requestId
            );

          if (
            pending
          ) {
            clearTimeout(
              pending.timer
            );

            pendingBrowseRequestsRef.current.delete(
              requestId
            );

            pending.reject(
              error instanceof Error
                ? error
                : new Error(
                    String(
                      error
                    )
                  )
            );
          }
        }


        return await responsePromise;
      },
      [
        user?.id,
        deviceStatus
      ]
    );

  const readTextFile =
    useCallback(
      async (
        filePath:
          string,

        jobId:
          string
      ): Promise<TextFileReadResultPayload> => {

        if (
          !isSupabaseConfigured
        ) {
          throw new Error(
            'Supabase is not configured'
          );
        }


        if (
          !user?.id
        ) {
          throw new Error(
            'Authentication required'
          );
        }


        const currentDevice =
          deviceRef.current;

        const channel =
          deviceChannelRef.current;


        if (
          !currentDevice ||
          !channel
        ) {
          throw new Error(
            'KAIZEN Agent is not connected'
          );
        }


        if (
          deviceStatus !==
            'online'
        ) {
          throw new Error(
            'KAIZEN Agent device is offline'
          );
        }


        const requestId =
          crypto.randomUUID();


        const request:
          TextFileReadRequestPayload = {

          requestId,

          jobId,

          path:
            filePath
        };


        const responsePromise =
          new Promise<TextFileReadResultPayload>(
            (
              resolve,
              reject
            ) => {

              const timer =
                setTimeout(
                  () => {

                    pendingTextFileRequestsRef.current.delete(
                      requestId
                    );


                    reject(
                      new Error(
                        'Report retrieval timed out'
                      )
                    );

                  },
                  12000
                );


              pendingTextFileRequestsRef.current.set(
                requestId,
                {
                  resolve,
                  reject,
                  timer
                }
              );
            }
          );


        try {

          const sendResult =
            await channel.send(
              {
                type:
                  'broadcast',

                event:
                  'text_file_read_request',

                payload: {

                  id:
                    `text-file-${requestId}`,

                  type:
                    'text_file_read_request',

                  source:
                    'web',

                  targetId:
                    currentDevice.id,

                  timestamp:
                    new Date().toISOString(),

                  payload:
                    request
                }
              }
            );


          if (
            sendResult !==
              'ok'
          ) {

            throw new Error(
              `Report retrieval dispatch failed: ${sendResult}`
            );
          }

        }
        catch (
          error:
            unknown
        ) {

          const pending =
            pendingTextFileRequestsRef.current.get(
              requestId
            );


          if (
            pending
          ) {

            clearTimeout(
              pending.timer
            );


            pendingTextFileRequestsRef.current.delete(
              requestId
            );


            pending.reject(
              error instanceof Error
                ? error
                : new Error(
                    String(
                      error
                    )
                  )
            );
          }
        }


        return await responsePromise;
      },
      [
        user?.id,
        deviceStatus
      ]
    );

  const getArtifactContent =
    useCallback(
      async (
        artifactId:
          string
      ): Promise<ArtifactContentResultPayload> => {
        if (
          !isSupabaseConfigured
        ) {
          throw new Error(
            'Supabase is not configured'
          );
        }

        if (
          !user?.id
        ) {
          throw new Error(
            'Authentication required'
          );
        }

        const currentDevice =
          deviceRef.current;

        const channel =
          deviceChannelRef.current;

        if (
          !currentDevice ||
          !channel
        ) {
          throw new Error(
            'KAIZEN Agent is not connected'
          );
        }

        if (
          deviceStatus !==
          'online'
        ) {
          throw new Error(
            'KAIZEN Agent device is offline'
          );
        }

        const requestId =
          crypto.randomUUID();

        const request:
          ArtifactContentRequestPayload = {
          requestId,
          artifactId
        };

        const responsePromise =
          new Promise<ArtifactContentResultPayload>(
            (
              resolve,
              reject
            ) => {
              const timer =
                setTimeout(
                  () => {
                    pendingArtifactRequestsRef.current.delete(
                      requestId
                    );

                    reject(
                      new Error(
                        'Artifact retrieval timed out'
                      )
                    );
                  },
                  10000
                );

              pendingArtifactRequestsRef.current.set(
                requestId,
                {
                  resolve,
                  reject,
                  timer
                }
              );
            }
          );

        try {
          const sendResult =
            await channel.send(
              {
                type:
                  'broadcast',

                event:
                  'artifact_content_request',

                payload: {
                  id:
                    `artifact-${requestId}`,

                  type:
                    'artifact_content_request',

                  source:
                    'web',

                  targetId:
                    currentDevice.id,

                  timestamp:
                    new Date().toISOString(),

                  payload:
                    request
                }
              }
            );

          if (
            sendResult !==
            'ok'
          ) {
            throw new Error(
              `Artifact retrieval dispatch failed: ${sendResult}`
            );
          }
        }
        catch (
          error:
            unknown
        ) {
          const pending =
            pendingArtifactRequestsRef.current.get(
              requestId
            );

          if (
            pending
          ) {
            clearTimeout(
              pending.timer
            );

            pendingArtifactRequestsRef.current.delete(
              requestId
            );

            pending.reject(
              error instanceof Error
                ? error
                : new Error(
                    String(
                      error
                    )
                  )
            );
          }
        }

        return await responsePromise;
      },
      [
        user?.id,
        deviceStatus
      ]
    );

  const sendJarvisMessage =
    useCallback(
      async (
        rawMessage:
          string,

        context?:
          {
            projectId?: string;
            workingDirectory?: string;
          }
      ): Promise<JarvisResponsePayload> => {

        if (
          !isSupabaseConfigured
        ) {

          throw new Error(
            'Supabase is not configured'
          );
        }


        if (
          !user?.id
        ) {

          throw new Error(
            'Authentication required'
          );
        }


        const currentDevice =
          deviceRef.current;


        const channel =
          deviceChannelRef.current;


        if (
          !currentDevice ||
          !channel
        ) {

          throw new Error(
            'KAIZEN Agent is not connected'
          );
        }


        if (
          deviceStatus !==
            'online'
        ) {

          throw new Error(
            'KAIZEN Agent device is offline'
          );
        }


        if (
          services.chatgptBridge !==
            'connected'
        ) {

          throw new Error(
            'JARVIS Chrome bridge is not ready'
          );
        }


        const message =
          rawMessage.trim();


        if (
          !message
        ) {

          throw new Error(
            'Message is empty'
          );
        }


        if (
          message.length >
            100000
        ) {

          throw new Error(
            'Message exceeds the 100,000 character JARVIS limit'
          );
        }


        const requestId =
          crypto.randomUUID();


        const request:
          JarvisRequestPayload = {
          requestId,

          message,

          projectId:
            context?.projectId,

          workingDirectory:
            context?.workingDirectory
        };


        const responsePromise =
          new Promise<JarvisResponsePayload>(
            (
              resolve,
              reject
            ) => {

              const timer =
                setTimeout(
                  () => {

                    pendingJarvisRequestsRef.current.delete(
                      requestId
                    );


                    setJarvisBusy(
                      false
                    );


                    reject(
                      new Error(
                        'JARVIS request timed out'
                      )
                    );

                  },
                  300000
                );


              pendingJarvisRequestsRef.current.set(
                requestId,
                {
                  resolve,
                  reject,
                  timer
                }
              );
            }
          );


        setJarvisBusy(
          true
        );


        try {

          const sendResult =
            await channel.send(
              {
                type:
                  'broadcast',

                event:
                  'assistant_request',

                payload: {
                  id:
                    `assistant-request-${requestId}`,

                  type:
                    'assistant_request',

                  source:
                    'web',

                  targetId:
                    currentDevice.id,

                  timestamp:
                    new Date().toISOString(),

                  payload:
                    request
                }
              }
            );


          if (
            sendResult !==
              'ok'
          ) {

            throw new Error(
              `JARVIS dispatch failed: ${sendResult}`
            );
          }

        }
        catch (
          error:
            unknown
        ) {

          const pending =
            pendingJarvisRequestsRef.current.get(
              requestId
            );


          if (
            pending
          ) {

            clearTimeout(
              pending.timer
            );


            pendingJarvisRequestsRef.current.delete(
              requestId
            );


            pending.reject(
              error instanceof Error
                ? error
                : new Error(
                    String(
                      error
                    )
                  )
            );
          }


          setJarvisBusy(
            false
          );
        }


        return await responsePromise;
      },
      [
        user?.id,
        deviceStatus,
        services.chatgptBridge
      ]
    );

  const sendJob =
    async (
      jobInput:
        Omit<
          Job,
          'id' |
          'userId' |
          'status' |
          'createdAt'
        >
    ): Promise<Job> => {
      if (
        !isSupabaseConfigured
      ) {
        throw new Error(
          'Supabase is not configured'
        );
      }

      if (
        !user
      ) {
        throw new Error(
          'Authentication is required'
        );
      }

      const currentDevice =
        deviceRef.current;

      let deviceChannel =
        deviceChannelRef.current;

      if (
        !currentDevice
      ) {
        throw new Error(
          'KAIZEN Agent device is unavailable'
        );
      }


      /*
       * KAIZEN REALTIME STARTUP RACE
       *
       * deviceStatus may become "online" from a fresh heartbeat
       * just before the private per-device realtime channel has
       * completed its subscription.
       *
       * Do not report the Agent as disconnected immediately.
       * Give the existing channel setup a short opportunity to
       * finish, while preserving the channel requirement.
       */
      if (
        !deviceChannel &&
        deviceStatus ===
          'online'
      ) {

        const channelWaitStartedAt =
          Date.now();


        while (
          !deviceChannel &&
          (
            Date.now() -
            channelWaitStartedAt
          ) <
            6000
        ) {

          await new Promise<void>(
            resolve => {

              window.setTimeout(
                resolve,
                150
              );
            }
          );


          deviceChannel =
            deviceChannelRef.current;
        }
      }


      if (
        !deviceChannel
      ) {
        throw new Error(
          deviceStatus ===
            'online'
            ? 'KAIZEN Agent realtime channel is still connecting'
            : 'KAIZEN Agent is not connected'
        );
      }

      if (
        deviceStatus !==
        'online'
      ) {
        throw new Error(
          'KAIZEN Agent device is offline'
        );
      }

      const jobId =
        crypto.randomUUID();

      const now =
        new Date().toISOString();

      const newJob:
        Job = {
        ...jobInput,

        id:
          jobId,

        userId:
          user.id,

        deviceId:
          currentDevice.id,

        status:
          'queued',

        createdAt:
          now
      };


      const {
        error:
          insertError
      } =
        await supabase
          .from(
            'kr_jobs'
          )
          .insert(
            {
              id:
                newJob.id,

              user_id:
                newJob.userId,

              device_id:
                newJob.deviceId,

              project_id:
                (newJob.projectId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(newJob.projectId))
                  ? newJob.projectId
                  : null,

              type:
                newJob.type,

              status:
                'queued',

              payload:
                newJob.payload,

              working_directory:
                newJob.workingDirectory,

              created_at:
                newJob.createdAt
            }
          );


      if (
        insertError
      ) {
        throw new Error(
          `Unable to create job: ${insertError.message}`
        );
      }


      setActiveJob(
        newJob
      );

      setActiveJobLogs(
        [
          {
            id:
              `system-${crypto.randomUUID()}`,

            sequence:
              0,

            stream:
              'system',

            data:
              `[KAIZEN Web] Job created: ${newJob.id}\n[Working Directory] ${newJob.workingDirectory}\n`,

            timestamp:
              now
          }
        ]
      );


      if (
        jobChannelRef.current
      ) {
        try {
          await jobChannelRef.current.unsubscribe();
        }
        catch {
          // cleanup only
        }

        jobChannelRef.current =
          null;
      }


      const jobChannel =
        supabase.channel(
          `job:${jobId}`,
          {
            config: {
              private:
                true,

              broadcast: {
                ack:
                  true
              }
            }
          }
        );


      jobChannel.on(
        'broadcast',
        {
          event:
            'job_output'
        },
        (
          {
            payload
          }
        ) => {
          setActiveJobLogs(
            previous => [
              ...previous,

              {
                id:
                  `log-${crypto.randomUUID()}`,

                sequence:
                  payload.sequence,

                stream:
                  payload.stream,

                data:
                  payload.data,

                timestamp:
                  payload.timestamp
              }
            ]
          );
        }
      );


      jobChannel.on(
        'broadcast',
        {
          event:
            'job_status_change'
        },
        (
          {
            payload
          }
        ) => {
          setActiveJob(
            previous =>
              previous
                ? {
                    ...previous,

                    status:
                      payload.status,

                    exitCode:
                      payload.exitCode,

                    error:
                      payload.error,

                    startedAt:
                      payload.status ===
                        'running'
                        ? payload.timestamp
                        : previous.startedAt,

                    finishedAt:
                      [
                        'succeeded',
                        'failed',
                        'cancelled',
                        'timed_out'
                      ].includes(
                        payload.status
                      )
                        ? payload.timestamp
                        : previous.finishedAt
                  }
                : previous
          );
        }
      );


      /*
       * CRITICAL ORDER:
       *
       * 1. DB job exists.
       * 2. Browser joins private job channel.
       * 3. ONLY THEN is the device notified.
       *
       * This prevents loss of the first stdout/status messages.
       */
      await subscribePrivateChannel(
        jobChannel
      );

      jobChannelRef.current =
        jobChannel;


      const offerResult =
        await deviceChannel.send(
          {
            type:
              'broadcast',

            event:
              'job_offer',

            payload: {
              id:
                `env-${crypto.randomUUID()}`,

              type:
                'job_offer',

              source:
                'web',

              timestamp:
                new Date().toISOString(),

              payload: {
                jobId
              }
            }
          }
        );


      if (
        offerResult !==
        'ok'
      ) {
        await supabase
          .from(
            'kr_jobs'
          )
          .update(
            {
              status:
                'failed',

              error:
                `Realtime job dispatch failed: ${offerResult}`,

              finished_at:
                new Date().toISOString()
            }
          )
          .eq(
            'id',
            jobId
          );

        throw new Error(
          `Realtime job dispatch failed: ${offerResult}`
        );
      }


      return newJob;
    };


  const cancelActiveJob =
    async (): Promise<boolean> => {
      const current =
        activeJobRef.current;

      const channel =
        deviceChannelRef.current;

      if (
        !current ||
        !channel
      ) {
        return false;
      }

      setActiveJobLogs(
        previous => [
          ...previous,

          {
            id:
              `cancel-${crypto.randomUUID()}`,

            sequence:
              previous.length +
              1,

            stream:
              'system',

            data:
              `\n[KAIZEN Web] Cancellation requested for ${current.id}\n`,

            timestamp:
              new Date().toISOString()
          }
        ]
      );


      const result =
        await channel.send(
          {
            type:
              'broadcast',

            event:
              'job_cancel',

            payload: {
              id:
                `env-${crypto.randomUUID()}`,

              type:
                'job_cancel',

              source:
                'web',

              timestamp:
                new Date().toISOString(),

              payload: {
                jobId:
                  current.id
              }
            }
          }
        );


      return result ===
        'ok';
    };


  return (
    <RealtimeContext.Provider
      value={{
        device,
        deviceStatus,
        services,

        latencyMs,

        wifiSsid,

        latencyQuality,

        activeJob,
        activeJobLogs,

        clearLogs,
        sendJob,
        cancelActiveJob,
        pingDevice,
        browseDirectory,
        getArtifactContent,

        readTextFile,
        sendJarvisMessage,
        jarvisChat,
        jarvisBusy,
        jarvisTransientState
      }}
    >
      {children}
    </RealtimeContext.Provider>
  );
};


export const useRealtime =
  (): RealtimeContextType => {
    const context =
      useContext(
        RealtimeContext
      );

    if (
      !context
    ) {
      throw new Error(
        'useRealtime must be used within a RealtimeProvider'
      );
    }

    return context;
  };

